import {
  PageTheme,
  UnifiedThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  pageTheme as defaultPageThemes,
  palettes,
  shapes,
} from '@backstage/theme';

import { alpha } from '@material-ui/core/styles';

/**
 * Design tokens shared by every DevHub design. Each design only describes
 * its look (colors, shape, depth); the component overrides are generated
 * from these tokens so all designs stay structurally consistent.
 */
interface DevHubDesignTokens {
  headerColors: string[];
  headerShape: string;
  headerFontColor: string;
  headerAccent?: string;
  radius: number;
  cardBorder: string;
  cardShadow: string;
  sidebarBorder: string;
  itemCardHeaderBackground: string;
  titleWeight: number;
}

const buildPageThemes = (tokens: DevHubDesignTokens): Record<string, PageTheme> => {
  const result: Record<string, PageTheme> = {};
  Object.keys(defaultPageThemes).forEach(key => {
    result[key] = genPageTheme({
      colors: tokens.headerColors,
      shape: tokens.headerShape,
      options: { fontColor: tokens.headerFontColor },
    });
  });
  return result;
};

const buildComponents = (tokens: DevHubDesignTokens): UnifiedThemeOptions['components'] => ({
  BackstageHeader: {
    styleOverrides: {
      header: ({ theme }) => ({
        boxShadow: 'none',
        paddingBottom: theme.spacing(2),
        borderBottom: tokens.headerAccent ? `2px solid ${tokens.headerAccent}` : 'none',
      }),
      title: ({ theme }) => ({
        color: theme.page.fontColor,
        fontWeight: tokens.titleWeight,
        letterSpacing: '-0.02em',
      }),
      subtitle: ({ theme }) => ({
        color: alpha(theme.page.fontColor, 0.85),
      }),
      type: ({ theme }) => ({
        color: alpha(theme.page.fontColor, 0.85),
      }),
    },
  },
  BackstageHeaderTabs: {
    styleOverrides: {
      defaultTab: {
        fontSize: 'inherit',
        textTransform: 'none',
        fontWeight: 600,
      },
    },
  },
  BackstageItemCardHeader: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.primary,
        backgroundColor: tokens.itemCardHeaderBackground,
        backgroundImage: 'none',
      }),
    },
  },
  BackstageSidebar: {
    styleOverrides: {
      drawer: {
        borderRight: tokens.sidebarBorder,
      },
    },
  },
  BackstageSidebarItem: {
    styleOverrides: {
      label: {
        overflow: 'visible',
        fontWeight: 500,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: tokens.radius,
      },
      elevation1: {
        boxShadow: tokens.cardShadow,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius,
        border: tokens.cardBorder,
        boxShadow: tokens.cardShadow,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: Math.max(tokens.radius - 4, 4),
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: Math.max(tokens.radius - 6, 4),
        fontWeight: 500,
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: Math.max(tokens.radius - 4, 4),
      },
    },
  },
});

const modernFontFamily = '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif';

/**
 * Aurora — bright, airy light design with an indigo/violet/sky gradient
 * header, soft elevated cards and a clean white sidebar.
 */
const auroraTokens: DevHubDesignTokens = {
  headerColors: ['#5B5BD6', '#8B5CF6', '#0EA5E9'],
  headerShape: shapes.wave2,
  headerFontColor: '#FFFFFF',
  radius: 14,
  cardBorder: '1px solid #E6E8F0',
  cardShadow: '0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 16px rgba(16, 24, 40, 0.06)',
  sidebarBorder: '1px solid #E6E8F0',
  itemCardHeaderBackground: alpha('#5B5BD6', 0.12),
  titleWeight: 800,
};

export const devHubAuroraTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: '#5B5BD6' },
    secondary: { main: '#0EA5E9' },
    info: { main: '#3B82F6' },
    warning: { main: '#F59E0B' },
    error: { main: '#E5484D' },
    success: { main: '#12B76A' },
    background: { default: '#F5F6FA', paper: '#FFFFFF' },
    navigation: {
      ...palettes.light.navigation,
      background: '#FFFFFF',
      indicator: '#5B5BD6',
      color: '#3F4354',
      selectedColor: '#5B5BD6',
      navItem: { hoverBackground: '#EEF0FF' },
      submenu: { background: '#F7F7FD' },
    },
  },
  fontFamily: modernFontFamily,
  pageTheme: buildPageThemes(auroraTokens),
  defaultPageTheme: 'home',
  components: buildComponents(auroraTokens),
});

/**
 * Midnight — deep slate dark design with CAE blue accents, a subtle accent line
 * under the header and flat, bordered "glass" cards.
 */
const midnightTokens: DevHubDesignTokens = {
  headerColors: ['#0F172A', '#163984'],
  headerShape: shapes.round,
  headerFontColor: '#F1F5F9',
  headerAccent: '#2969F2',
  radius: 12,
  cardBorder: '1px solid rgba(148, 163, 184, 0.14)',
  cardShadow: 'none',
  sidebarBorder: '1px solid rgba(148, 163, 184, 0.12)',
  itemCardHeaderBackground: alpha('#2969F2', 0.18),
  titleWeight: 700,
};

export const devHubMidnightTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: { main: '#5B8DF6' },
    secondary: { main: '#A78BFA' },
    info: { main: '#608FF4' },
    warning: { main: '#FBBF24' },
    error: { main: '#F87171' },
    success: { main: '#34D399' },
    background: { default: '#0B1120', paper: '#111827' },
    navigation: {
      ...palettes.dark.navigation,
      background: '#070B14',
      indicator: '#2969F2',
      color: '#94A3B8',
      selectedColor: '#A7C1F9',
      navItem: { hoverBackground: alpha('#2969F2', 0.18) },
      submenu: { background: '#0B1120' },
    },
  },
  fontFamily: modernFontFamily,
  pageTheme: buildPageThemes(midnightTokens),
  defaultPageTheme: 'home',
  components: buildComponents(midnightTokens),
});

/**
 * Horizon — corporate CAE design: navy sidebar, CAE blue wave header and
 * crisp, compact cards for information-dense pages.
 */
const horizonTokens: DevHubDesignTokens = {
  headerColors: ['#0A1F44', '#1F5FD6'],
  headerShape: shapes.wave,
  headerFontColor: '#FFFFFF',
  radius: 6,
  cardBorder: '1px solid #DDE3EC',
  cardShadow: '0 1px 3px rgba(10, 31, 68, 0.08)',
  sidebarBorder: 'none',
  itemCardHeaderBackground: alpha('#1F5FD6', 0.1),
  titleWeight: 700,
};

export const devHubHorizonTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: '#1F5FD6' },
    secondary: { main: '#00A3A1' },
    info: { main: '#2969F2' },
    warning: { main: '#FFAB00' },
    error: { main: '#E53935' },
    success: { main: '#2CBE7C' },
    background: { default: '#F3F5F8', paper: '#FFFFFF' },
    navigation: {
      ...palettes.light.navigation,
      background: '#0A1F44',
      indicator: '#4FA3FF',
      color: '#C7D2E5',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: '#133063' },
      submenu: { background: '#0F2A57' },
    },
  },
  fontFamily: '"Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
  pageTheme: buildPageThemes(horizonTokens),
  defaultPageTheme: 'home',
  components: buildComponents(horizonTokens),
});
